import React from 'react';
import Box, { BoxProps } from '@mui/material/Box';
import Grid, { GridProps } from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

/**
 * The placeholders a page holds while its data loads. Each one takes the footprint of what it
 * stands in for, so nothing moves when the answer arrives.
 */

/** A `StatusCardWidget`'s height: its label over an h4 figure. */
export const STATUS_CARD_HEIGHT = 106;

/** A `DashboardSection` with its title and caption still to come. */
export const SectionSkeleton: React.FC<{
  titleWidth?: number;
  caption?: boolean;
  sx?: BoxProps['sx'];
  children: React.ReactNode;
}> = ({ titleWidth = 200, caption = true, sx, children }) => (
  <Box sx={[{ p: { xs: 2, md: 3 }, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
    <Stack gap={{ xs: 2, md: 3 }} height="100%">
      <Stack gap={0.25}>
        <Skeleton variant="text" width={titleWidth} height={28} />
        {caption && <Skeleton variant="text" width={titleWidth * 1.4} height={18} />}
      </Stack>
      {children}
    </Stack>
  </Box>
);

/** A row of cards laid out on the grid the real cards use. */
export const CardGridSkeleton: React.FC<{
  count: number;
  height: number;
  item: Pick<GridProps, 'xs' | 'sm' | 'md' | 'lg'>;
  spacing?: number;
}> = ({ count, height, item, spacing = 2 }) => (
  <Grid container spacing={spacing}>
    {Array.from({ length: count }, (_, index) => (
      <Grid item {...item} key={index}>
        <Skeleton variant="rounded" height={height} />
      </Grid>
    ))}
  </Grid>
);

/** A data grid: its header, then a few rows at the grid's row height. */
export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 6 }) => (
  <Stack gap={0.5}>
    <Skeleton variant="rounded" height={56} />
    {Array.from({ length: rows }, (_, index) => (
      <Skeleton key={index} variant="rounded" height={48} />
    ))}
  </Stack>
);

/** A list of label, meter and figure rows. */
export const MeterListSkeleton: React.FC<{ rows?: number; gap?: number }> = ({
  rows = 5,
  gap = 1.25,
}) => (
  <Stack gap={gap}>
    {Array.from({ length: rows }, (_, index) => (
      <Stack key={index} direction="row" alignItems="center" gap={1.5}>
        <Skeleton variant="text" sx={{ flex: '1 1 40%', minWidth: 0 }} height={20} />
        <Skeleton variant="rounded" sx={{ flex: '1 1 45%' }} height={10} />
        <Skeleton variant="text" width={48} height={20} />
      </Stack>
    ))}
  </Stack>
);
